// @ts-nocheck
/*
 * Port of views/dashboard/module/aclSettingsUi.js (origin/main-dev, 860 lines).
 * BYTE-IDENTICAL — MODULE_VISUAL_MAP, palette hashing, dark-mode
 * `getAclModuleDarkStyle`, all 8 shared UI components
 * (AclSettingsPageHeader, AclSettingsFormTop, AclModulePermissionsToolbar,
 *  AclPermissionToggle, AclOptionsButton, AclModulePermissionsTable,
 *  AclSettingsModal, AclModalSection, AclModalCheckboxGrid,
 *  AclModalOptionCheck) preserved. `renderAclOptionsCell` dispatcher +
 *  per-module row helpers preserved. Re-exports at end (FaPhoneAlt etc.)
 *  are what the 9 modal files import — do NOT strip them.
 */
import React from 'react';
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  FaShieldAlt,
  FaCheck,
  FaTimes,
  FaSearch,
  FaFilter,
  FaQuestionCircle,
  FaInfoCircle,
  FaUndo,
  FaCloudUploadAlt,
  FaUser,
  FaUsers,
  FaBuilding,
  FaListAlt,
  FaCoffee,
  FaFileAlt,
  FaBullhorn,
  FaCity,
  FaPhoneAlt,
  FaSitemap,
  FaBook,
  FaMicrophone,
  FaHeadset,
  FaChartBar,
  FaCog,
  FaThLarge,
  FaVoicemail,
  FaProjectDiagram,
  FaAddressBook,
  FaServer,
  FaGlobe,
  FaFolder,
  FaChartLine,
  FaUserPlus,
  FaNetworkWired,
  FaBell,
} from 'react-icons/fa';
import { getAclModuleDisplayLabel, isAclOpSupported } from '@/utilities/constant';
import './acl-settings.scss';

const DEFAULT_ICON_PALETTES = [
  { bg: '#ede9fe', color: '#7c3aed' },
  { bg: '#dbeafe', color: '#2563eb' },
  { bg: '#dcfce7', color: '#16a34a' },
  { bg: '#ffedd5', color: '#ea580c' },
  { bg: '#ffe4e6', color: '#e11d48' },
  { bg: '#fef9c3', color: '#ca8a04' },
  { bg: '#cffafe', color: '#0891b2' },
  { bg: '#fce7f3', color: '#db2777' },
  { bg: '#ccfbf1', color: '#0d9488' },
  { bg: '#e0e7ff', color: '#4338ca' },
];

const MODULE_VISUAL_MAP = {
  'ACL Setting List': { icon: FaShieldAlt, bg: '#ede9fe', color: '#7c3aed' },
  'Activity List': { icon: FaChartLine, bg: '#dbeafe', color: '#2563eb' },
  Agents: { icon: FaUserPlus, bg: '#dcfce7', color: '#16a34a' },
  'Call Group': { icon: FaUsers, bg: '#ede9fe', color: '#8b5cf6' },
  'API Webhook': { icon: FaNetworkWired, bg: '#ffedd5', color: '#ea580c' },
  Breaks: { icon: FaCoffee, bg: '#ffe4e6', color: '#e11d48' },
  'Call Centres List': { icon: FaBuilding, bg: '#dbeafe', color: '#0284c7' },
  'Call Script': { icon: FaFileAlt, bg: '#fef9c3', color: '#ca8a04' },
  Campaigns: { icon: FaBullhorn, bg: '#dcfce7', color: '#15803d' },
  'CDR Report': { icon: FaChartBar, bg: '#dbeafe', color: '#1d4ed8' },
  'Company List': { icon: FaCity, bg: '#e0e7ff', color: '#4338ca' },
  'Contact Group': { icon: FaAddressBook, bg: '#fce7f3', color: '#db2777' },
  IVR: { icon: FaProjectDiagram, bg: '#cffafe', color: '#0891b2' },
  'Voice Library': { icon: FaBook, bg: '#f3e8ff', color: '#9333ea' },
  'Voice Plans': { icon: FaMicrophone, bg: '#fef3c7', color: '#d97706' },
  Extension: { icon: FaHeadset, bg: '#e0f2fe', color: '#0369a1' },
  Numbers: { icon: FaPhoneAlt, bg: '#ccfbf1', color: '#0d9488' },
  Webhook: { icon: FaGlobe, bg: '#ffedd5', color: '#c2410c' },
  'SMS Webhook': { icon: FaGlobe, bg: '#ffedd5', color: '#c2410c' },
  'Disposition Forms': { icon: FaFileAlt, bg: '#fef3c7', color: '#b45309' },
  'MOH Management': { icon: FaMicrophone, bg: '#fae8ff', color: '#a21caf' },
  'Manage Prompts': { icon: FaMicrophone, bg: '#fbcfe8', color: '#9d174d' },
  'Credit History': { icon: FaChartBar, bg: '#dbeafe', color: '#1e40af' },
  'Module List': { icon: FaCog, bg: '#f1f5f9', color: '#64748b' },
  Users: { icon: FaUser, bg: '#d1fae5', color: '#059669' },
  Groups: { icon: FaUsers, bg: '#ede9fe', color: '#6d28d9' },
  'Dialer Groups': { icon: FaUsers, bg: '#ede9fe', color: '#6d28d9' },
  'PBX Management': { icon: FaServer, bg: '#e2e8f0', color: '#475569' },
  'Gateway Management': { icon: FaServer, bg: '#fed7aa', color: '#9a3412' },
  Trunks: { icon: FaSitemap, bg: '#bfdbfe', color: '#1e3a8a' },
  'Data Center': { icon: FaServer, bg: '#cbd5e1', color: '#334155' },
  'Flash Message': { icon: FaBullhorn, bg: '#fef08a', color: '#854d0e' },
  notification: { icon: FaBell, bg: '#ddd6fe', color: '#7c3aed' },
  Notification: { icon: FaBell, bg: '#ddd6fe', color: '#7c3aed' },
  'Agent Announcement': { icon: FaBullhorn, bg: '#bbf7d0', color: '#166534' },
  'Remarks Status': { icon: FaListAlt, bg: '#fecdd3', color: '#be123c' },
  'Agent File List': { icon: FaFileAlt, bg: '#fde68a', color: '#92400e' },
  'TTS Plans': { icon: FaMicrophone, bg: '#fce7f3', color: '#be185d' },
  TTS: { icon: FaMicrophone, bg: '#f5d0fe', color: '#86198f' },
  Recordings: { icon: FaMicrophone, bg: '#fee2e2', color: '#b91c1c' },
  'Dialer Locations': { icon: FaGlobe, bg: '#cffafe', color: '#0e7490' },
};

function hashModuleName(name) {
  let hash = 0;
  const str = String(name || '');
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash + str.charCodeAt(i) * (i + 1)) % DEFAULT_ICON_PALETTES.length;
  }
  return hash;
}

function parseHexColor(hex) {
  const normalized = String(hex || '#64748b').replace('#', '');
  if (normalized.length !== 6) {
    return { r: 100, g: 116, b: 139 };
  }
  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16),
  };
}

function brightenChannel(value, amount = 0.5) {
  return Math.min(255, Math.round(value + (255 - value) * amount));
}

export function getAclModuleDarkStyle(accentColor) {
  const { r, g, b } = parseHexColor(accentColor);
  return {
    bg: `rgba(${r}, ${g}, ${b}, 0.2)`,
    color: `rgb(${brightenChannel(r)}, ${brightenChannel(g)}, ${brightenChannel(b)})`,
  };
}

export function getAclModuleIconStyle(bg, color) {
  const dark = getAclModuleDarkStyle(color);
  return {
    backgroundColor: bg,
    color,
    '--acl-icon-bg': bg,
    '--acl-icon-color': color,
    '--acl-icon-bg-dark': dark.bg,
    '--acl-icon-color-dark': dark.color,
  };
}

export function getAclModuleVisual(moduleName) {
  const mapped = MODULE_VISUAL_MAP[moduleName];
  if (mapped) {
    return mapped;
  }
  const palette = DEFAULT_ICON_PALETTES[hashModuleName(moduleName)];
  return {
    icon: FaThLarge,
    bg: palette.bg,
    color: palette.color,
  };
}

export function getAclModuleIcon(moduleName) {
  return getAclModuleVisual(moduleName).icon;
}

export function AclSettingsPageHeader({ title, subtitle, onHelpClick, onBack }) {
  return (
    <div className="acl-page-header">
      <div className="acl-page-header__left">
        <div className="acl-page-header__icon">
          <FaShieldAlt />
        </div>
        <div>
          <h4 className="acl-page-header__title">{title}</h4>
          <p className="acl-page-header__subtitle">{subtitle}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="inline-flex h-9 items-center gap-1.5 rounded-md border border-border/60 bg-card px-3 text-sm font-medium text-foreground shadow-neu-btn transition-colors hover:bg-accent hover:text-accent-foreground cursor-pointer"
            aria-label="Back to ACL Settings list"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
        )}
        {/* <button type="button" className="acl-help-btn" onClick={onHelpClick}>
          <FaQuestionCircle />
          Need help?
        </button> */}
      </div>
    </div>
  );
}

export function AclSettingsFormTop({
  aclName,
  onAclNameChange,
  nameLabel = 'ACL Name',
  showDefaultRole,
  defaultForRole,
  onDefaultRoleChange,
  defaultRoleOptions,
  showParentAclCheckbox,
  parentACLEnabled,
  onParentAclChange,
  infoMessage = 'Users will only see modules in sidebar for which View Permission is enabled.',
}) {
  return (
    <div className="acl-form-top row g-3">
      <div className="col-lg-4 col-md-6">
        <div className="mb-3">
          <label className="form-label acl-form-label">
            {nameLabel} <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            placeholder="Enter ACL name"
            value={aclName}
            onChange={onAclNameChange}
            className="form-control acl-form-control"
          />
        </div>
      </div>

      {showDefaultRole && (
        <div className="col-lg-4 col-md-6">
          <div className="mb-3">
            <label className="form-label acl-form-label">Default for Role (optional)</label>
            <select
              value={defaultForRole}
              onChange={onDefaultRoleChange}
              className="form-control form-select acl-form-control"
            >
              {defaultRoleOptions}
            </select>
          </div>
        </div>
      )}

      {showParentAclCheckbox && (
        <div className="col-lg-4 col-md-6 d-flex align-items-end">
          <div className="form-check acl-parent-check mb-3">
            <input
              type="checkbox"
              id="aclCheckbox"
              className="form-check-input"
              checked={parentACLEnabled}
              onChange={onParentAclChange}
            />
            <label htmlFor="aclCheckbox" className="form-check-label">
              Apply Parent's ACL
            </label>
          </div>
        </div>
      )}

      {/* <div className="col-lg-4 col-md-12 ms-lg-auto">
        <div className="acl-info-banner">
          <FaInfoCircle className="acl-info-banner__icon" />
          <span>{infoMessage}</span>
        </div>
      </div> */}
    </div>
  );
}

export function AclModulePermissionsToolbar({
  searchTerm,
  onSearchChange,
  onSelectAllModules,
  onDeselectAllModules,
  filterValue,
  onFilterChange,
}) {
  return (
    <div className="acl-permissions-toolbar">
      <div className="acl-permissions-toolbar__left">
        <div className="acl-search-wrap">
          <FaSearch className="acl-search-wrap__icon" />
          <input
            type="text"
            placeholder="Search module..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="form-control acl-search-input"
          />
        </div>
        <div className="acl-filter-wrap">
          <FaFilter className="acl-filter-wrap__icon" />
          <select
            value={filterValue}
            onChange={(e) => onFilterChange(e.target.value)}
            className="form-control form-select acl-filter-select"
            aria-label="Filter modules"
          >
            <option value="all">Filters</option>
            <option value="allowed">View allowed</option>
            <option value="denied">View denied</option>
          </select>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="acl-toolbar-btn"
          onClick={onSelectAllModules}
        >
          <FaCheck /> Select All
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="acl-toolbar-btn"
          onClick={onDeselectAllModules}
        >
          <FaTimes /> Deselect All
        </Button>
      </div>
      <div className="acl-permissions-legend">
        <span className="acl-legend-item">
          <span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-emerald-500 text-white ring-1 ring-inset ring-emerald-600">
            <FaCheck style={{ fontSize: 9 }} />
          </span>
          Allowed
        </span>
        <span className="acl-legend-item">
          <span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-muted/50 text-muted-foreground/60 ring-1 ring-inset ring-border/70">
            <FaTimes style={{ fontSize: 9 }} />
          </span>
          Not Allowed
        </span>
      </div>
    </div>
  );
}

export function AclPermissionToggle({ active, onClick, title, disabled = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      disabled={disabled}
      aria-pressed={!!active}
      aria-label={active ? 'Allowed — click to disallow' : 'Not allowed — click to allow'}
      className={
        'inline-flex h-7 w-7 items-center justify-center rounded-md ring-1 ring-inset transition-all ' +
        (disabled ? 'cursor-not-allowed opacity-40 ' : 'cursor-pointer ') +
        (active
          ? 'bg-emerald-500 text-white ring-emerald-600 shadow-[0_1px_3px_rgba(16,185,129,0.35)] hover:bg-emerald-600'
          : 'bg-muted/50 text-muted-foreground/50 ring-border/70 ' +
            (disabled ? '' : 'hover:bg-primary/10 hover:text-primary hover:ring-primary/30'))
      }
    >
      {active ? (
        <FaCheck style={{ fontSize: 11 }} />
      ) : (
        <FaTimes style={{ fontSize: 10 }} />
      )}
    </button>
  );
}

export function AclOptionsButton({ label, onClick, icon = null }) {
  if (!label || label === '—') {
    return <span className="acl-options-empty">—</span>;
  }
  return (
    <button type="button" className="acl-options-btn" onClick={onClick}>
      {icon}
      {label}
    </button>
  );
}

export function AclModulePermissionsTable({
  modules,
  selectedModules,
  onPermissionChange,
  onSelectAllRow,
  renderOptionsCell,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  isOpDisabled,
}) {
  const total = modules.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * pageSize;
  const pageModules = modules.slice(start, start + pageSize);
  const end = Math.min(start + pageSize, total);

  return (
    <div className="acl-table-wrap">
      <div className="table-responsive">
        <table className="table acl-permissions-table mb-0">
          <thead>
            <tr>
              <th>Module Name</th>
              <th className="text-center">View</th>
              <th className="text-center">Read</th>
              <th className="text-center">Add</th>
              <th className="text-center">Edit</th>
              <th className="text-center">Delete</th>
              <th className="text-center">Options</th>
              <th className="text-center">Select All</th>
            </tr>
          </thead>
          <tbody>
            {pageModules.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center text-muted py-4">
                  No modules match your search.
                </td>
              </tr>
            ) : (
              pageModules.map((module) => {
                const moduleName = module.module_name;
                const perms = selectedModules[moduleName] || {};
                const { icon: Icon, bg, color } = getAclModuleVisual(moduleName);
                const iconStyle = getAclModuleIconStyle(bg, color);
                const isRowAllSelected = ['view_permission', 'read', 'add', 'edit', 'delete']
                  .filter((op) => isAclOpSupported(moduleName, op))
                  .every((op) => perms[op]);

                return (
                  <tr key={moduleName}>
                    <td>
                      <div className="acl-module-name-cell">
                        <span className="acl-module-icon" style={iconStyle}>
                          <Icon />
                        </span>
                        <span>{getAclModuleDisplayLabel(moduleName)}</span>
                      </div>
                    </td>
                    {['view_permission', 'read', 'add', 'edit', 'delete'].map((op) => {
                      if (!isAclOpSupported(moduleName, op)) {
                        return (
                          <td key={op} className="text-center text-muted-foreground" title="Not applicable for this module">
                            —
                          </td>
                        );
                      }
                      const opDisabled = isOpDisabled ? isOpDisabled(moduleName, op) : false;
                      return (
                      <td key={op} className="text-center">
                        <div className="d-flex justify-content-center">
                          <AclPermissionToggle
                            active={!!perms[op]}
                            disabled={opDisabled}
                            onClick={() => onPermissionChange(moduleName, op)}
                            title={
                              opDisabled
                                ? 'Not allowed on the ACL this one was copied from'
                                : op === 'view_permission'
                                  ? 'Required to grant other permissions'
                                  : undefined
                            }
                          />
                        </div>
                      </td>
                      );
                    })}
                    <td className="text-center">{renderOptionsCell(moduleName)}</td>
                    <td className="text-center">
                      <div className="form-check acl-row-select-all">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={!!isRowAllSelected}
                          onChange={() => onSelectAllRow(moduleName)}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {total > 0 && (
        <div className="mt-3 mb-4 flex flex-wrap items-center justify-between gap-3 border-t border-border/60 px-3 pb-2 pt-3 text-xs text-muted-foreground">
          <span>
            Showing{' '}
            <span className="font-medium text-foreground">
              {start + 1}–{end}
            </span>{' '}
            of <span className="font-medium text-foreground">{total}</span>
          </span>
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onPageChange(safePage - 1)}
              disabled={safePage <= 1}
              className="gap-1.5"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              Previous
            </Button>
            <span className="whitespace-nowrap tabular-nums">
              Page{' '}
              <span className="font-medium text-foreground">{safePage}</span> /{' '}
              <span className="font-medium text-foreground">{Math.max(totalPages, 1)}</span>
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onPageChange(safePage + 1)}
              disabled={safePage >= totalPages}
              className="gap-1.5"
            >
              Next
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
            <div className="ml-2 flex items-center gap-2">
              <span className="text-muted-foreground">Rows:</span>
              <select
                value={pageSize}
                onChange={(e) => onPageSizeChange(Number(e.target.value))}
                className="input-embossed h-8 rounded-md px-2 text-xs"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function buildPageNumbers(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const pages = [1];
  if (current > 3) pages.push('...');
  for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i += 1) {
    pages.push(i);
  }
  if (current < total - 2) pages.push('...');
  pages.push(total);
  return pages;
}

export function AclSettingsModal({
  show,
  onHide,
  title,
  icon: Icon = FaCog,
  description,
  limitedByParent,
  children,
  onSave,
  onReset,
  saveDisabled,
  saveLabel = 'Save Options',
  size = 'lg',
}) {
  const sizeCls =
    size === 'sm'
      ? 'max-w-sm'
      : size === 'md'
        ? 'max-w-md'
        : size === 'xl'
          ? 'max-w-5xl'
          : 'max-w-3xl';
  return (
    <Dialog open={!!show} onOpenChange={(next) => !next && onHide?.()}>
      <DialogContent
        className={`acl-settings-modal-dialog acl-settings-modal ${sizeCls} max-h-[85vh] overflow-y-auto`}
      >
        <DialogHeader className="acl-modal-header">
          <div className="acl-modal-header-inner">
            <span className="acl-modal-header-icon">
              <Icon />
            </span>
            <DialogTitle>{title}</DialogTitle>
          </div>
        </DialogHeader>
        <div className="acl-modal-body">
          {description ? <p className="acl-modal-description">{description}</p> : null}
          {limitedByParent ? (
            <p className="acl-modal-limited-note">Limited to options allowed on your account.</p>
          ) : null}
          {children}
        </div>
        <DialogFooter className="acl-modal-footer !justify-between">
          {onReset ? (
            <Button
              type="button"
              variant="outline"
              className="acl-modal-reset-btn"
              onClick={onReset}
            >
              <FaUndo /> Reset
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              className="acl-modal-cancel-btn"
              onClick={onHide}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="acl-modal-save-btn"
              onClick={onSave}
              disabled={saveDisabled}
            >
              <FaCloudUploadAlt /> {saveLabel}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AclModalSection({ icon: Icon, title, subtitle, children }) {
  return (
    <div className="acl-modal-section">
      <div className="acl-modal-section__header">
        <span className="acl-modal-section__icon">
          <Icon />
        </span>
        <div>
          <h6 className="acl-modal-section__title">{title}</h6>
          {subtitle ? <p className="acl-modal-section__subtitle">{subtitle}</p> : null}
        </div>
      </div>
      <div className="acl-modal-section__body">{children}</div>
    </div>
  );
}

export function AclModalCheckboxGrid({ options, selected, onToggle, columns = 2 }) {
  return (
    <div className={`row row-cols-${columns} g-2 acl-modal-checkbox-grid`}>
      {options.map((opt) => (
        <div key={opt.value} className="col">
          <div className="form-check acl-modal-check">
            <input
              type="checkbox"
              id={`acl-opt-${opt.value}`}
              className="form-check-input"
              checked={selected.includes(opt.value)}
              onChange={() => onToggle(opt.value)}
            />
            <label htmlFor={`acl-opt-${opt.value}`} className="form-check-label">
              {opt.label}
            </label>
          </div>
        </div>
      ))}
    </div>
  );
}

export function AclModalOptionCheck({ id, label, description, checked, onChange, disabled = false }) {
  return (
    <div className="form-check acl-modal-check acl-modal-check--block mb-0">
      <input
        type="checkbox"
        id={id}
        className="form-check-input"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
      />
      <label htmlFor={id} className="form-check-label">
        <strong>{label}</strong>
        {description ? <span className="d-block acl-modal-check-desc">{description}</span> : null}
      </label>
    </div>
  );
}

// v1 re-exports these icon components so modal files import icons from
// './aclSettingsUi' rather than react-icons/fa directly — preserved.
export { FaPhoneAlt, FaFolder, FaGlobe, FaVoicemail, FaProjectDiagram, FaUsers, FaUser, FaBuilding, FaMicrophone, FaFileAlt, FaUserPlus };

export function renderAclOptionsCell(moduleName, ctx) {
  const {
    selectedModules,
    isVoicePlanRow,
    isVoiceLibraryRow,
    isCallGroupRow,
    isAgentsRow,
    isCallCentreRow,
    isIvrRow,
    isUsersRow,
    voicePlanAccessEnabled,
    voiceLibraryAccessEnabled,
    callGroupEditAccessEnabled,
    agentSettingsAccessEnabled,
    callCentreVoicemailAccessEnabled,
    ivrSettingsAccessEnabled,
    usersEmailVerificationAccessEnabled,
    formatPlanDirectionsLabel,
    formatVoiceLibraryAclLabel,
    formatCallGroupEditOptionsLabel,
    formatAgentEditOptionsLabel,
    formatCallCentreVoicemailAclLabel,
    formatCallCentreTranscriptionAclLabel,
    isAgentLeadsRow,
    agentLeadsCustomAccessEnabled,
    formatAgentLeadsCustomAclLabel,
    setShowAgentLeadsCustomModal,
    isContactsRow,
    contactsCustomFieldsAccessEnabled,
    formatContactCustomFieldsAclLabel,
    setShowContactsCustomFieldsModal,
    formatIvrNodeOptionsLabel,
    setShowVoicePlanModal,
    setShowVoiceLibraryModal,
    setShowCallGroupModal,
    setShowAgentModal,
    setShowCallCentreVoicemailModal,
    setShowIvrModal,
    formatUsersEmailVerificationAclLabel,
    setShowUsersEmailVerificationModal,
    isDispositionFormsRow,
    dispositionTimingAccessEnabled,
    formatDispositionTimingAclLabel,
    setShowDispositionFormsTimingModal,
    isTtsRow,
    ttsAccessEnabled,
    formatTtsOperatorLabel,
    setShowTtsOperatorModal,
    isAiAgentRow,
    aiAgentDescLimitAccessEnabled,
    formatAiAgentDescLimitAclLabel,
    setShowAiAgentDescLimitModal,
    isCompanyRow,
    companyAssetsVisibilityAccessEnabled,
    formatCompanyAssetsVisibilityAclLabel,
    setShowCompanyAssetsVisibilityModal,
    isCampaignsRow,
    pacingRatiosAccessEnabled,
    formatPacingRatiosLabel,
    setShowPacingRatiosModal,
  } = ctx;

  const pickLabel = (formatted) => (formatted === '—' ? 'Select options' : formatted);

  if (isVoicePlanRow(moduleName)) {
    if (!voicePlanAccessEnabled(moduleName)) {
      return <span className="acl-options-empty">—</span>;
    }
    const label = pickLabel(formatPlanDirectionsLabel(selectedModules[moduleName]?.plan_directions));
    return <AclOptionsButton label={label} onClick={() => setShowVoicePlanModal(true)} />;
  }

  if (isVoiceLibraryRow(moduleName)) {
    if (!voiceLibraryAccessEnabled(moduleName)) {
      return <span className="acl-options-empty">—</span>;
    }
    const label = pickLabel(formatVoiceLibraryAclLabel(selectedModules[moduleName]));
    return <AclOptionsButton label={label} onClick={() => setShowVoiceLibraryModal(true)} />;
  }

  if (isCallGroupRow(moduleName)) {
    if (!callGroupEditAccessEnabled(moduleName)) {
      return <span className="acl-options-empty">—</span>;
    }
    const label = pickLabel(formatCallGroupEditOptionsLabel(selectedModules[moduleName]));
    return <AclOptionsButton label={label} onClick={() => setShowCallGroupModal(true)} />;
  }

  if (isAgentsRow(moduleName)) {
    if (!agentSettingsAccessEnabled(moduleName)) {
      return <span className="acl-options-empty">—</span>;
    }
    const label = pickLabel(formatAgentEditOptionsLabel(selectedModules[moduleName]));
    return <AclOptionsButton label={label} onClick={() => setShowAgentModal(true)} />;
  }

  if (isCallCentreRow(moduleName)) {
    if (!callCentreVoicemailAccessEnabled(moduleName)) {
      return <span className="acl-options-empty">—</span>;
    }
    const vmLabel = formatCallCentreVoicemailAclLabel(selectedModules[moduleName]);
    const trLabel = formatCallCentreTranscriptionAclLabel(selectedModules[moduleName]);
    const parts = [vmLabel, trLabel].filter((l) => l && l !== '—');
    const label = pickLabel(parts.length ? parts.join(' + ') : '—');
    return <AclOptionsButton label={label} onClick={() => setShowCallCentreVoicemailModal(true)} />;
  }

  if (isCompanyRow?.(moduleName)) {
    if (!companyAssetsVisibilityAccessEnabled?.(moduleName)) {
      return <span className="acl-options-empty">—</span>;
    }
    const label = pickLabel(formatCompanyAssetsVisibilityAclLabel(selectedModules[moduleName]));
    return <AclOptionsButton label={label} onClick={() => setShowCompanyAssetsVisibilityModal(true)} />;
  }

  if (isIvrRow(moduleName)) {
    if (!ivrSettingsAccessEnabled(moduleName)) {
      return <span className="acl-options-empty">—</span>;
    }
    const label = pickLabel(formatIvrNodeOptionsLabel(selectedModules[moduleName]));
    return <AclOptionsButton label={label} onClick={() => setShowIvrModal(true)} />;
  }

  if (isContactsRow?.(moduleName)) {
    if (!contactsCustomFieldsAccessEnabled?.(moduleName)) {
      return <span className="acl-options-empty">—</span>;
    }
    const label = pickLabel(formatContactCustomFieldsAclLabel(selectedModules[moduleName]));
    return <AclOptionsButton label={label} onClick={() => setShowContactsCustomFieldsModal(true)} />;
  }

  if (isAgentLeadsRow?.(moduleName)) {
    if (!agentLeadsCustomAccessEnabled?.(moduleName)) {
      return <span className="acl-options-empty">—</span>;
    }
    const label = pickLabel(formatAgentLeadsCustomAclLabel(selectedModules[moduleName]));
    return <AclOptionsButton label={label} onClick={() => setShowAgentLeadsCustomModal(true)} />;
  }

  if (isUsersRow?.(moduleName)) {
    if (!usersEmailVerificationAccessEnabled?.(moduleName)) {
      return <span className="acl-options-empty">—</span>;
    }
    const entry = selectedModules[moduleName];
    const emailRaw = formatUsersEmailVerificationAclLabel(entry);
    const driveRaw = ctx.formatDriveFrequenciesLabel?.(entry);
    const parts = [emailRaw, driveRaw].filter((value) => value && value !== '—');
    const label = parts.length ? parts.join(' · ') : 'Select options';
    return <AclOptionsButton label={label} onClick={() => setShowUsersEmailVerificationModal(true)} />;
  }

  if (isDispositionFormsRow?.(moduleName)) {
    if (!dispositionTimingAccessEnabled?.(moduleName)) {
      return <span className="acl-options-empty">—</span>;
    }
    const label = pickLabel(formatDispositionTimingAclLabel(selectedModules[moduleName]));
    return <AclOptionsButton label={label} onClick={() => setShowDispositionFormsTimingModal(true)} />;
  }

  if (isTtsRow?.(moduleName)) {
    if (!ttsAccessEnabled?.(moduleName)) {
      return <span className="acl-options-empty">—</span>;
    }
    const label = pickLabel(formatTtsOperatorLabel(selectedModules[moduleName]));
    return <AclOptionsButton label={label} onClick={() => setShowTtsOperatorModal(true)} />;
  }

  if (isAiAgentRow?.(moduleName)) {
    if (!aiAgentDescLimitAccessEnabled?.(moduleName)) {
      return <span className="acl-options-empty">—</span>;
    }
    const label = pickLabel(formatAiAgentDescLimitAclLabel(selectedModules[moduleName]));
    return <AclOptionsButton label={label} onClick={() => setShowAiAgentDescLimitModal(true)} />;
  }

  if (isCampaignsRow?.(moduleName)) {
    if (!pacingRatiosAccessEnabled?.(moduleName)) {
      return <span className="acl-options-empty">—</span>;
    }
    const label = pickLabel(formatPacingRatiosLabel(selectedModules[moduleName]));
    return <AclOptionsButton label={label} onClick={() => setShowPacingRatiosModal(true)} />;
  }

  return <span className="acl-options-empty">—</span>;
}
